import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTitle } from 'react-use';

import { ResponsiveSidebarPanel } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { EmptyRules } from '@/modules/rules/components/EmptyRules';
import { RulePanel } from '@/modules/rules/components/RulePanel/RulePanel';
import { RulesContextualHelp } from '@/modules/rules/components/RulesContextualHelp';
import { RulesFooter } from '@/modules/rules/components/RulesFooter';
import RulesHeader from '@/modules/rules/components/RulesHeader/RulesHeader';
import { RulesList } from '@/modules/rules/components/RulesList';
import { RulesToolbar } from '@/modules/rules/components/RulesToolbar';
import { NEW_RULE_ID, RENAME_MODE } from '@/modules/rules/constants';
import { useRules } from '@/modules/rules/hooks/useRules';
import { useRulesFilters } from '@/modules/rules/hooks/useRulesFilters';
import { getPermissions, selectPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

export const Rules = () => {
  const { t } = useTranslation();
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'rules', actions.WRITE));
  const permissions = useSelector(selectPermissions);

  const { desk } = useDesks(deskId);
  const { rules, reorderRule, reorderStatus, listStatus } = useRules(deskId);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    filters,
    filteredRules,
    setFilter,
    removeFilter,
    resetFilters,
    activeFilterCount,
    savedViews,
    activeView,
    hasUnsavedChanges,
    saveView,
    updateView,
    deleteView,
    renameView,
    applyView,
    clearActiveView,
  } = useRulesFilters(rules?.rules);

  useTitle(t('pages.rules', { 0: desk?.name ?? '-' }));

  useSegments(ruleId ? PageName.RULE : PageName.RULES);

  const term = searchTerm.trim().toLowerCase();
  const displayedRules = term
    ? filteredRules?.filter(
        (rule) =>
          rule.name.toLowerCase().includes(term) || rule.description?.toLowerCase().includes(term)
      )
    : filteredRules;

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: `rules:${deskId}`,
    isLoading,
    isEmpty: rules?.rules?.length === 0,
  });
  const isFiltering = !!term || activeFilterCount > 0;
  const hasNoResults = isFiltering && displayedRules?.length === 0;

  const rulesPath = `/${slug}/environments/${deskId}/rules`;
  const isRename = canWrite && ruleId !== NEW_RULE_ID && searchParams.get('mode') === RENAME_MODE;
  const closePanel = () => navigate(rulesPath);

  const getPanelTitle = () => {
    if (ruleId === NEW_RULE_ID) {
      return t('rules.create_rule');
    }
    return isRename ? t('rules.rename_rule') : t('rules.edit_rule');
  };

  if (ruleId === NEW_RULE_ID && permissions && !canWrite) {
    return <Navigate to={rulesPath} replace />;
  }

  return (
    <SidebarLayout
      autoSaveId="rules-sidebar"
      sidebar={
        ruleId && (
          <ResponsiveSidebarPanel
            title={getPanelTitle()}
            onClose={closePanel}
            onDismiss={closePanel}
          >
            <RulePanel
              key={`${ruleId}-${isRename}`}
              ruleId={ruleId}
              isRename={isRename}
              onClose={closePanel}
              onOpenEdit={() => navigate(`${rulesPath}/${ruleId}`)}
            />
          </ResponsiveSidebarPanel>
        )
      }
    >
      <RulesHeader isEmpty={isEmptyPage} />
      <PageContentWrapper readOnly={!canWrite} noGutters className="px-6 pb-6">
        <div className="mx-auto max-w-xl pt-5">
          <RulesContextualHelp />

          {showEmpty && <EmptyRules />}

          {showEmptySkeleton && <EmptySkeleton descriptionLines={3} link />}

          {showList && (
            <>
              <RulesToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={filters}
                setFilter={setFilter}
                removeFilter={removeFilter}
                resetFilters={resetFilters}
                activeFilterCount={activeFilterCount}
                savedViews={savedViews}
                activeView={activeView}
                hasUnsavedChanges={hasUnsavedChanges}
                onSaveView={saveView}
                onUpdateView={updateView}
                onDeleteView={deleteView}
                onRenameView={renameView}
                onApplyView={applyView}
                onClearActiveView={clearActiveView}
              />

              <RulesList
                rules={displayedRules}
                totalRulesCount={rules?.rules?.length ?? 0}
                reorderRule={reorderRule}
                isReordering={reorderStatus === 'pending'}
                isLoading={isLoading}
                isFiltering={isFiltering}
              />

              {!hasNoResults && <RulesFooter />}
            </>
          )}
        </div>
      </PageContentWrapper>
    </SidebarLayout>
  );
};
